import type { CSSProperties, HTMLAttributes, Ref } from "react";
import { DESK_KIND_LABEL, type DeskItem } from "../../lib/deskItems.ts";
import { useIsMac } from "../../lib/useIsMac.ts";

interface DeskItemCardProps extends HTMLAttributes<HTMLDivElement> {
  item: DeskItem;
  ref: Ref<HTMLDivElement>;
  style: CSSProperties;
}

type ContentItem = Exclude<DeskItem, { kind: "search" }>;

function cardKicker(item: ContentItem): string {
  return item.kind === "project" ? item.meta : DESK_KIND_LABEL[item.kind];
}

function cardBody(item: ContentItem): string {
  return item.kind === "stack" ? item.list.join(" · ") : item.body;
}

function ariaLabel(item: DeskItem): string {
  if (item.kind === "search") return "Search the vault, prompts and ritual. Press Enter to search, arrow keys to move.";
  return `${DESK_KIND_LABEL[item.kind]}: ${item.title}. Press Enter to read, arrow keys to move.`;
}

/** Looks like a search input; activating the card opens the command palette. */
function SearchFace({ title }: { title: string }) {
  const isMac = useIsMac();
  return (
    <>
      <span className="home-card-pin" aria-hidden="true" />
      <span className="home-card-kind">{DESK_KIND_LABEL.search}</span>
      <span className="home-card-title">{title}</span>
      <span className="home-search-slot" aria-hidden="true">
        <span className="home-search-slot-icon">⌕</span>
        <span className="home-search-slot-text">Search the vault, prompts &amp; ritual…</span>
        <kbd>{isMac ? "⌘" : "Ctrl"}</kbd>
        <kbd>K</kbd>
      </span>
    </>
  );
}

function ContentFace({ item }: { item: ContentItem }) {
  return (
    <>
      {item.kind === "hat" && <span className="home-card-pin" aria-hidden="true" />}
      <span className="home-card-kind">{cardKicker(item)}</span>
      <span className="home-card-title">{item.title}</span>
      <span className="home-card-body">{cardBody(item)}</span>
    </>
  );
}

/** One pinned card on the home desk. Position and drag handlers come from the desk. */
export function DeskItemCard({ item, ref, className = "", ...rest }: DeskItemCardProps) {
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel(item)}
      className={`home-card home-card--${item.kind} ${className}`.trim()}
      {...rest}
    >
      {item.kind === "search" ? <SearchFace title={item.title} /> : <ContentFace item={item} />}
    </div>
  );
}
